import { normalizeEmotion } from "../../utils/emotion";
import { useRef, useState, useEffect } from "react";
import { clamp, randInt } from "./chunk-0.jsx";
import { pickChamiiCuteProfile } from "./chunk-9.jsx";
import { ChamiiCuteEye } from "./chunk-11.jsx";
export function ChamiiCuteEyes({
  emotion,
  variant,
  accent,
  poke = 0,
  active = true,
  motionBoost = false
}) {
  const e = normalizeEmotion(emotion);
  const profile = pickChamiiCuteProfile(emotion, variant, accent);
  const sparkle = profile.sparkleColor;
  const blinkTimerRef = useRef(null);
  const [blinkToken, setBlinkToken] = useState({
    id: 0,
    kind: 'single'
  });
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const pickKind = () => {
      if (e === 'sleepy') return 'slow';
      const canWink = !profile.winkLeft && e !== 'winking';
      const r = Math.random();
      if (r < (motionBoost ? 0.16 : 0.12)) return 'double';
      if (canWink && r < (motionBoost ? 0.24 : 0.18)) return Math.random() < 0.5 ? 'winkLeft' : 'winkRight';
      return 'single';
    };
    const nextIntervalMs = () => {
      let min = motionBoost ? 1600 : 2200;
      let max = motionBoost ? 3100 : 4200;
      if (e === 'sleepy') {
        min = motionBoost ? 2600 : 3800;
        max = motionBoost ? 4400 : 6500;
      } else if (e === 'laughing') {
        min = motionBoost ? 1200 : 1700;
        max = motionBoost ? 2300 : 3100;
      } else if (e === 'angry') {
        min += 300;
        max += 500;
      }
      return min + randInt(Math.max(1, Math.floor(max - min)));
    };
    const schedule = () => {
      blinkTimerRef.current = setTimeout(() => {
        if (cancelled) return;
        const kind = pickKind();
        setBlinkToken(prev => ({
          id: prev.id + 1,
          kind
        }));
        schedule();
      }, nextIntervalMs());
    };
    schedule();
    return () => {
      cancelled = true;
      if (blinkTimerRef.current) clearTimeout(blinkTimerRef.current);
    };
  }, [active, e, motionBoost, profile.winkLeft]);
  useEffect(() => {
    if (!poke) return;
    setBlinkToken(prev => ({
      id: prev.id + 1,
      kind: motionBoost ? 'double' : 'single'
    }));
  }, [poke, motionBoost]);
  const keyframes = `
    @keyframes chamiiBlinkOnce{0%{transform:scaleY(0)}30%{transform:scaleY(1)}60%{transform:scaleY(1)}100%{transform:scaleY(0)}}
    @keyframes chamiiBlinkDouble{0%{transform:scaleY(0)}18%{transform:scaleY(1)}36%{transform:scaleY(0)}52%{transform:scaleY(0)}68%{transform:scaleY(1)}84%{transform:scaleY(0)}100%{transform:scaleY(0)}}
    @keyframes chamiiBlinkSlow{0%{transform:scaleY(0)}22%{transform:scaleY(1)}72%{transform:scaleY(1)}100%{transform:scaleY(0)}}
    @keyframes chamiiGaze{0%,100%{transform:translate3d(0,0,0)}24%{transform:translate3d(-6%,3%,0)}48%{transform:translate3d(5%,-2%,0)}72%{transform:translate3d(-3%,-6%,0)}}
    @keyframes chamiiSaccade{0%,62%,100%{transform:translate3d(0,0,0)}64%{transform:translate3d(2%,-1%,0)}66%{transform:translate3d(-1%,1%,0)}68%{transform:translate3d(0,0,0)}}
    @keyframes chamiiFloat{0%,100%{transform:translate3d(0,0,0)}50%{transform:translate3d(0,1px,0)}}
    @keyframes chamiiTwinkle{0%,100%{transform:scale(1) rotate(0deg); opacity:.9}50%{transform:scale(1.08) rotate(8deg); opacity:1}}
    @keyframes chamiiJelly{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}
  `;
  return <div className="relative w-full h-full">
      <div className="w-full h-full flex items-center justify-center">
        <div className="relative w-[78%] h-[56%] flex items-center justify-center animate-[chamiiFloat_2.6s_ease-in-out_infinite]" style={{
        gap: `${profile.gap}%`
      }}>
          <ChamiiCuteEye side="left" profile={profile} blinkToken={blinkToken} active={active} motionBoost={motionBoost} seedKey={`chamii:${profile.variant}:${emotion}:left`} />
          <ChamiiCuteEye side="right" profile={profile} blinkToken={blinkToken} active={active} motionBoost={motionBoost} seedKey={`chamii:${profile.variant}:${emotion}:right`} />

          {profile.blush ? <>
              <div className="absolute left-[18%] bottom-[-10%] w-[14%] h-[10%] rounded-full bg-pink-500/25 blur-[1px]" />
              <div className="absolute right-[18%] bottom-[-10%] w-[14%] h-[10%] rounded-full bg-pink-500/25 blur-[1px]" />
            </> : null}

          {profile.tear ? <div className="absolute left-[26%] bottom-[-14%] w-[6%] h-[18%] bg-sky-300/70 rounded-b-full rounded-t-[45%] rotate-[10deg]" /> : null}

          {profile.sparkle ? <>
              <ChamiiSparkle className="absolute -left-[4%] top-[4%]" color={sparkle} />
              <ChamiiSparkle className="absolute -right-[2%] top-[18%] scale-90" color={sparkle} />
              <ChamiiSparkle className="absolute left-[48%] -top-[10%] scale-75" color={sparkle} />
            </> : null}
        </div>
      </div>
      <style>{keyframes}</style>
    </div>;
}
export function ChamiiSparkle({
  className = '',
  color = '#ffffff'
}) {
  return <svg viewBox="0 0 24 24" className={`w-6 h-6 ${className}`.trim()} style={{
    fill: color,
    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.35))',
    animation: 'chamiiTwinkle 1.8s ease-in-out infinite',
    willChange: 'transform'
  }} aria-hidden="true">
      <path d="M12 1.8l1.7 6.2 6.2 1.7-6.2 1.7L12 17.6l-1.7-6.2-6.2-1.7 6.2-1.7L12 1.8z" />
    </svg>;
}
export function ChamiiCutePupil({
  kind,
  color,
  sizePercent
}) {
  const size = `${Math.round(clamp(sizePercent, 6, 90))}%`;
  if (kind === 'heart') {
    return <svg viewBox="0 0 32 32" className="absolute left-1/2 top-1/2" style={{
      width: size,
      height: size,
      transform: 'translate(-50%,-50%)'
    }}>
        <path d="M16 28s-9.3-5.7-12.6-11C.8 12.3 3.2 7.7 7.7 7.2c2.3-.3 4.5.9 5.9 2.6 1.4-1.7 3.6-2.9 5.9-2.6 4.5.5 6.9 5.1 4.3 9.8C25.3 22.3 16 28 16 28z" fill={color} />
      </svg>;
  }
  if (kind === 'star') {
    return <div className="absolute left-1/2 top-1/2" style={{
      width: size,
      height: size,
      transform: 'translate(-50%,-50%)',
      background: color,
      clipPath: 'polygon(50% 0%, 62% 26%, 90% 35%, 70% 55%, 76% 84%, 50% 70%, 24% 84%, 30% 55%, 10% 35%, 38% 26%)',
      borderRadius: 2
    }} />;
  }
  if (kind === 'slit') {
    return <div className="absolute left-1/2 top-1/2 rounded-full" style={{
      width: `${Math.max(8, Math.round(sizePercent * 0.35))}%`,
      height: size,
      transform: 'translate(-50%,-50%)',
      background: color
    }} />;
  }
  if (kind === 'line') {
    return <div className="absolute left-1/2 top-1/2" style={{
      width: `${Math.max(22, Math.round(sizePercent * 1.1))}%`,
      height: `${Math.max(16, Math.round(sizePercent * 0.8))}%`,
      transform: 'translate(-50%,-30%)',
      borderBottom: `6px solid ${color}`,
      borderRadius: '0 0 999px 999px',
      opacity: 0.9
    }} />;
  }
  return <div className="absolute left-1/2 top-1/2 rounded-full" style={{
    width: size,
    height: size,
    transform: 'translate(-50%,-50%)',
    background: color
  }} />;
}
